import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import applicationApi from '../../services/api/application.api';

interface Application {
  id: string;
  name: string;
  description?: string;
  environment: 'development' | 'staging' | 'production';
  language?: string;
  framework?: string;
  status: 'active' | 'inactive';
}

interface ApplicationState {
  availableApplications: Application[];
  selectedApplicationId: string | null; // null = "All Applications"
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

const getSelectedApplicationFromStorage = (): string | null => {
  return localStorage.getItem('selectedApplicationId') || null;
};

const initialState: ApplicationState = {
  availableApplications: [],
  selectedApplicationId: getSelectedApplicationFromStorage(),
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunk to load user's available applications
export const loadUserApplications = createAsyncThunk(
  'application/loadUserApplications',
  async (_, { rejectWithValue }) => {
    try {
      const result = await applicationApi.getAvailable();

      if (!result.success) {
        throw new Error('Failed to load applications');
      }

      return result.data as Application[];
    } catch (error: any) {
      console.error('[Redux] loadUserApplications error:', error);
      return rejectWithValue(error.message || 'Failed to load applications');
    }
  }
);

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    setSelectedApplication(state, action: PayloadAction<string | null>) {
      console.log('[Redux] setSelectedApplication - old:', state.selectedApplicationId, 'new:', action.payload);
      state.selectedApplicationId = action.payload;
      if (action.payload) {
        localStorage.setItem('selectedApplicationId', action.payload);
      } else {
        localStorage.removeItem('selectedApplicationId');
      }
      console.log('[Redux] setSelectedApplication - state updated to:', state.selectedApplicationId);
    },
    clearApplicationSelection(state) {
      state.selectedApplicationId = null;
      localStorage.removeItem('selectedApplicationId');
    },
    clearApplications(state) {
      state.availableApplications = [];
      state.selectedApplicationId = null;
      state.lastUpdated = null;
      localStorage.removeItem('selectedApplicationId');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUserApplications.fulfilled, (state, action) => {
        state.availableApplications = action.payload;
        state.loading = false;
        state.lastUpdated = new Date();

        // If selected app is no longer available, reset to null
        if (
          state.selectedApplicationId &&
          !action.payload.find((app) => app.id === state.selectedApplicationId)
        ) {
          state.selectedApplicationId = null;
          localStorage.removeItem('selectedApplicationId');
        }
      })
      .addCase(loadUserApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load applications';
      });
  },
});

export const {
  setSelectedApplication,
  clearApplicationSelection,
  clearApplications
} = applicationSlice.actions;

export default applicationSlice.reducer;

// Selectors
export const selectCurrentApplication = (state: RootState) =>
  state.application.selectedApplicationId;

export const selectAvailableApplications = (state: RootState) =>
  state.application.availableApplications;

export const selectApplicationLoading = (state: RootState) =>
  state.application.loading;

export const selectApplicationError = (state: RootState) =>
  state.application.error;

export const selectCurrentApplicationObject = (state: RootState) => {
  if (!state.application.selectedApplicationId) {
    return null;
  }
  return state.application.availableApplications.find(
    (app) => app.id === state.application.selectedApplicationId
  );
};
